import { selectedPartnerProductOrderCartItemsState } from "@/atoms/item/selected-partner-product-order-cart-items-state";
import { createOrderState } from "@/atoms/order/create-order-state";
import { orderDetailState } from "@/atoms/order/order-detail-state";
import { profileState } from "@/atoms/user/profile-state";
import { useInitKCP } from "@/common/custom-hook/use-init-kcp";
import { ButtonColor } from "@/common/enum/button-color";
import { PartnerOrderPath } from "@/common/enum/path";
import { PayCurrency } from "@/common/enum/pay-currency";
import { PayMethod } from "@/common/enum/pay-method";
import { ReceiptState } from "@/page/order/PartnerCreateOrder/_componant/VirtualAccountInput";
import { orderService } from "@/services/order-service2";
import { kcpAuthDtoKeys } from "@/types/dto/kcp-auth-dto";
import { OrderDetailDto } from "@/types/dto/order-detail-dto";
import { CreateOrderPayload } from "@/types/payload/create-order-payload";
import { PaymentPayload } from "@/types/payload/payment-payload";
import { Button } from "flowbite-react";
import { debounce } from "lodash";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { ordererState } from "../../atoms/quote/orderer-state";
import { previewState } from "../../atoms/quote/preview-state";

interface Props {
  payMethod: PayMethod;
  disabled: boolean;
  receiptState: ReceiptState;
}

// 결제하기 버튼있는 부분
export const PartnerPaymentButton: React.FC<Props> = ({
  payMethod,
  disabled,
  receiptState,
}) => {
  const navigate = useNavigate();
  const payload = useRecoilValue(createOrderState);
  const profile = useRecoilValue(profileState);
  const formRef = useRef<HTMLFormElement>(null);
  const [orderDetail, setOrderDetail] = useRecoilState(orderDetailState);
  const cartItems = useRecoilValue(selectedPartnerProductOrderCartItemsState);

  const preview = useRecoilValue(previewState);
  const orderer = useRecoilValue(ordererState);

  const isButtonActive =
    !disabled &&
    !!orderer.ordererName &&
    !!orderer.ordererBusinessName &&
    !!orderer.ordererPhone &&
    !!orderer.ordererMail &&
    !!payload.receiverName &&
    !!payload.receiverPhone &&
    !!payload.receiverEmailId &&
    !!payload.receiverEmailDomain &&
    !!payload.receiverPostalCode &&
    (!!payload.receiverJibun || !!payload.receiverRoad) &&
    payMethod &&
    preview &&
    preview.cartItems.length > 0;

  const handleButtonClick = async () => {
    if (!orderer || !preview) return;

    const createOrderPayload: CreateOrderPayload = {
      ordererName: orderer.ordererName,
      ordererBusinessName: orderer.ordererBusinessName,
      ordererPhone: orderer.ordererPhone,
      ordererMail: orderer.ordererMail,
      receiverName: payload.receiverName,
      receiverPhone: payload.receiverPhone,
      receiverEmail:
        payload.receiverEmailId + "@" + payload.receiverEmailDomain,
      receiverPostalCode: payload.receiverPostalCode,
      receiverAddress: payload.receiverJibun || payload.receiverRoad,
      receiverAddressDetail: payload.receiverAddressDetail,
      receiverMemo: payload.receiverMemo,
      constructionName: orderer.constructionName,
      constructionWorkName: orderer.constructionWorkName,
      items: cartItems.map((item) => ({
        itemId: item.childItem.id,
        quantity: item.quantity,
        memoItems: item.childItem.memoItems,
      })),
      quotationId: preview.id || undefined,
    };
    const response = await orderService.create(createOrderPayload);
    if (!response.ok) {
      return alert("발주 생성 실패");
    }
    const orderId = response.data.id;
    const orderResponse = await orderService.getById(orderId);
    if (!orderResponse.ok) {
      return alert("발주 조회 실패");
    }

    setOrderDetail(orderResponse.data);
    handlePayment(orderResponse.data);
  };

  async function createPaymentForPayLater(orderDetail: OrderDetailDto) {
    const payment: PaymentPayload = {
      enc_data: "",
      enc_info: "",
      tran_cd: "",
      ordr_idxx: orderDetail?.invoiceNumber.toString(),
      pay_method: PayMethod.PAY_LATER,
    };
    const response = await orderService.payment(orderDetail.id, payment);
    if (!response.ok) {
      alert("후불결제에 실패하였습니다.");
      return;
    }
    const orderResponse = await orderService.getById(orderDetail.id);
    if (!orderResponse.ok) {
      alert("후불결제 발주서 조회에 실패하였습니다.");
      return;
    }
    setOrderDetail(orderResponse.data);
    navigate(PartnerOrderPath.COMPLETE_ORDER);
  }

  async function createPaymentForVC(orderDetail: OrderDetailDto) {
    const payment: PaymentPayload = {
      enc_data: "",
      enc_info: "",
      tran_cd: "",
      ordr_idxx: orderDetail?.invoiceNumber.toString(),
      pay_method: PayMethod.VIRTUAL_ACCOUNT,
      receiptType: receiptState.type === "personal" ? "0" : "1",
      taxNumber: receiptState.taxNumber.replace(/-/g, ""),
    };
    const response = await orderService.payment(orderDetail.id, payment);
    if (!response.ok) {
      alert("가상계좌 결제에 실패하였습니다.");
      return;
    }
    const orderResponse = await orderService.getById(orderDetail.id);
    if (!orderResponse.ok) {
      alert("가상계좌 발주서 조회에 실패하였습니다.");
      return;
    }
    setOrderDetail(orderResponse.data);
    navigate(PartnerOrderPath.COMPLETE_ORDER);
  }
  // 결제창 호출 함수
  const handlePayment = async (orderDetail: OrderDetailDto) => {
    if (!orderDetail) return;
    if (orderDetail?.payment.id !== 0) return;
    if (!formRef.current) return;

    formRef.current.pay_method.value = payMethod;
    formRef.current.ordr_idxx.value = orderDetail.invoiceNumber.toString();
    formRef.current.good_name.value = orderDetail.invoiceNumber.toString();
    formRef.current.good_mny.value = orderDetail.finalPriceSum.toString();

    if (payMethod === PayMethod.PAY_LATER) {
      await createPaymentForPayLater(orderDetail);
      return;
    }

    if (payMethod === PayMethod.VIRTUAL_ACCOUNT) {
      await createPaymentForVC(orderDetail);
      return;
    }

    try {
      window.KCP_Pay_Execute_Web(formRef.current);
      console.log("KCP Payment executed");
    } catch (e) {
      console.error("Error during KCP Payment:", e);
      alert("KCP 모듈 실행에 실패하였습니다.");
    }
  };

  const debouncedCompletePayment = debounce(async (FormOrJson, closeEvent) => {
    if (!orderDetail) return;

    const frm = formRef.current;
    if (!frm) return;

    kcpAuthDtoKeys.forEach((key) => {
      const inputElement = frm[key] as HTMLInputElement | null;
      if (inputElement) {
        inputElement.value = FormOrJson[key]?.value || "";
      }
    });

    const originTaxNumber =
      receiptState.taxNumber?.trim() ||
      (receiptState.type === "business"
        ? profile.businessNumber
        : profile.phone);

    const payment: PaymentPayload = {
      enc_data: frm.enc_data.value,
      enc_info: frm.enc_info.value,
      tran_cd: frm.tran_cd.value,
      ordr_idxx: frm.ordr_idxx.value,
      pay_method: frm.pay_method.value,
      receiptType: receiptState.type === "personal" ? "0" : "1",
      taxNumber: originTaxNumber.replace(/-/g, ""),
    };

    if (frm.res_cd.value === "0000") {
      const response = await orderService.payment(orderDetail.id, payment);
      if (!response.ok) {
        closeEvent();
        alert("결제에 실패하였습니다. (T0001)");
        return;
      }
      const orderResponse = await orderService.getById(orderDetail.id);
      if (!orderResponse.ok) {
        closeEvent();
        alert("발주서 조회에 실패하였습니다.");
        return;
      }
      closeEvent();
      setOrderDetail(orderResponse.data);
      navigate(PartnerOrderPath.COMPLETE_ORDER);
    } else {
      closeEvent();
      alert("결제에 실패하였습니다. (T0002)");
    }
  }, 300);

  window.m_Completepayment = debouncedCompletePayment;

  useInitKCP();

  // useEffect(() => {
  //   if (orderDetail?.payment.id === 0) {
  //     handlePayment();
  //   }
  // }, [orderDetail]);

  if (!profile) return null;

  return (
    <div>
      <form
        ref={formRef}
        name="order_info"
        method="post"
        action="/your-api-endpoint" // 승인요청 URL
      >
        {/* 주문 정보 */}
        {/* KCP 발급 사이트(상점)코드
        테스트코드 : T0000 */}
        <input
          type="hidden"
          name="site_cd"
          value={process.env.REACT_APP_KCP_SITE_CD}
        />

        {/* 상점관리 주문번호
        (유니크한 값 설정 권장)
        ※ 주문번호는 영문과 숫자로 설정하셔야합니다.
        TODO 추후 invoiceNumber로 지정 */}
        <input
          type="hidden"
          name="ordr_idxx"
          defaultValue={orderDetail?.invoiceNumber.toString()}
          maxLength={40}
        />

        {/* 상품명
        ※ 고객안내 및 주문정보 관리를 위해 정확한 상품명으로 설정하셔야 합니다. */}
        <input
          type="hidden"
          name="good_name"
          defaultValue={orderDetail?.invoiceNumber.toString()}
        />

        {/* good_mny
        주문요청금액(결제 창으로 전달하는 금액으로 결제 창에 보여지는 실제 결제금액이 아닙니다.)
        결제 금액은 숫자 이외의 문자(콤마 등)는 허용 하지 않습니다.
        결제창이 닫힌 후 ordr_chk 값에 리턴되는 금액정보가 변경되는지 체크하셔야 됩니다.
        ※ 화폐단위가 ‘USD’ 일 경우, Cent까지 설정
        ex ) $10.55 일 경우 콤마를 뺀 1055 $100 일 경우 10000로 설정 */}
        <input
          type="hidden"
          name="good_mny"
          defaultValue={orderDetail?.finalPriceSum.toString()}
          maxLength={9}
        />

        {/* 화폐단위
        ※ 원화 - WON : 410 / 달러 - USD : 840
        ※ 달러의 경우 NHN KCP에 별도의 신청(국제 세미나, 국제 학술대회 등만 신청가능)해야 사용 가능합니다. */}
        <input
          type="hidden"
          name="currency"
          defaultValue={PayCurrency.WON}
          maxLength={3}
        />

        {/* 쇼핑몰에서 관리하는 회원 ID
        ※ 유니크한 값으로 입력
        기관에 따라 리스크 관리 조치를 위한 쇼핑몰 관리 ID를 필수로 요청
        휴대폰 소액결제 - 40Byte
        상품권결제 - 20Byte */}
        <input
          type="hidden"
          name="shop_user_id"
          defaultValue={profile.id.toString()}
          maxLength={20}
        />

        <input type="hidden" name="buyr_name" defaultValue={profile.name} />
        <input type="hidden" name="buyr_tel2" defaultValue={profile.phone} />
        <input type="hidden" name="buyr_mail" defaultValue={profile.email} />

        {/* 인증데이터 처리 */}
        <input type="hidden" name="pay_method" />
        <input type="hidden" name="res_cd" />
        <input type="hidden" name="res_msg" />
        <input type="hidden" name="enc_info" />
        <input type="hidden" name="enc_data" />
        <input type="hidden" name="ret_pay_method" />
        <input type="hidden" name="tran_cd" />
        <input type="hidden" name="use_pay_method" />
      </form>

      <div className="flex justify-center">
        <Button
          size="xl"
          color={isButtonActive ? ButtonColor.PRIMARY : ""}
          disabled={!isButtonActive}
          className="w-[240px] mt-6"
          onClick={handleButtonClick}
        >
          결제하기
        </Button>
      </div>
    </div>
  );
};
